/**
 * Nearbeee — Enhanced Design Prototype
 * Drop into any React + Tailwind project to preview.
 * All mock data and logic unchanged. Only visuals enhanced.
 *
 * Inspiration: Zomato cards · Swiggy header · CRED dark theme
 *              Blinkit urgency · Urban Company categories
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Search, MapPin, Navigation, MessageCircle, Tag, Store, User,
  ArrowLeft, Star, Coffee, Smartphone, Shirt, ShoppingBasket,
  Wrench, Scissors, Stethoscope, Scale, Armchair, Dumbbell,
  Car, MoreHorizontal, Flame, X, Mic, Eye, EyeOff, Mail,
  Check, AlertCircle, LogOut, LayoutDashboard, TrendingUp,
  PlusSquare, Settings, ChevronDown, Bell, Zap, Heart,
  SlidersHorizontal, Palette, Percent, Filter, Send, Bot,
  ChevronRight, Clock, ShieldCheck
} from 'lucide-react';

// ─────────────────────────────────────────
// MOCK DATA  (identical to original)
// ─────────────────────────────────────────
const generateReviews = (r) => [
  { id: 1, user: 'Aman K.',   rating: r,              date: '2025-10-12', text: 'Absolutely fantastic experience. The offer was exactly as described.' },
  { id: 2, user: 'Priya S.',  rating: Math.max(1,r-1),date: '2025-10-05', text: 'Good overall, but it was quite crowded when I visited.' },
  { id: 3, user: 'Vikram R.', rating: 5,              date: '2025-09-28', text: 'Highly recommend this place! Great value for money.' },
  { id: 4, user: 'Neha M.',   rating: r,              date: '2025-09-15', text: 'Polite staff and quick service. Will definitely return.' },
  { id: 5, user: 'Rohan D.',  rating: Math.max(1,r-0.5),date:'2025-09-02',text: 'Very convenient and authentic deals. Happy with the purchase.' },
];

const mockOffers = [
  { id:1, shopName:'Chai Pani',         category:'Food & Beverage',    title:'Buy 1 Get 1 Free on all Biryanis',       description:"Special weekend offer. Applicable on Chicken, Mutton, and Veg Biryani. Dine-in and Takeaway available.", distance:1.2, validUntil:'Sep 26, 2025', rating:4.8, reviews:842,  icon:'🍲', discountPercent:50,  endingToday:false, reviewsList:generateReviews(5),   menu:[{id:101,name:'Chicken Dum Biryani',category:'Biryanis',originalPrice:'₹300',offerPrice:'₹150',tag:'Best Seller'},{id:102,name:'Mutton Biryani',category:'Biryanis',originalPrice:'₹400',offerPrice:'₹200',tag:''},{id:103,name:'Paneer Tikka',category:'Starters',originalPrice:'₹250',offerPrice:'₹220',tag:'Veg'}]},
  { id:2, shopName:'Gupta Electronics', category:'Electronics',         title:'Flat ₹500 off on Smartwatches',          description:'Show this offer at the counter. Valid on Boat, Noise, and Fireboltt models.',                       distance:2.5, validUntil:'Oct 10, 2025', rating:4.5, reviews:315,  icon:'⌚', discountPercent:20,  endingToday:false, reviewsList:generateReviews(4.5),menu:[{id:201,name:'Noise ColorFit Pro',category:'Smartwatches',originalPrice:'₹2,499',offerPrice:'₹1,999',tag:'Limited'},{id:202,name:'Boat Airdopes',category:'Audio',originalPrice:'₹1,499',offerPrice:'₹1,299',tag:'Audio'}]},
  { id:3, shopName:'Glow & Go Salon',   category:'Salons & Spas',      title:'Flat 40% Off on Premium Hair Spa',       description:"Rejuvenate your hair with our L'Oreal premium hair spa treatment.",                                distance:4.1, validUntil:'Nov 01, 2025', rating:4.7, reviews:512,  icon:'✂️', discountPercent:40,  endingToday:false, reviewsList:generateReviews(4.7),menu:[{id:301,name:'Premium Hair Spa',category:'Hair Care',originalPrice:'₹1,500',offerPrice:'₹900',tag:'Offer'},{id:302,name:'Basic Haircut (Men)',category:'Hair Care',originalPrice:'₹250',offerPrice:'₹200',tag:''},{id:303,name:'Fruit Facial',category:'Skin Care',originalPrice:'₹800',offerPrice:'₹650',tag:'Skin'}]},
  { id:4, shopName:'City Health Clinic',category:'Doctors & Clinics',  title:'Free First Consultation',                description:'Book your first appointment for free. Blood tests and diagnostics available at 20% off.',            distance:8.5, validUntil:'Today, 8:00 PM',rating:4.9, reviews:1205, icon:'🩺', discountPercent:100, endingToday:true,  reviewsList:generateReviews(5),   menu:[{id:401,name:'First Consultation',category:'Consultations',originalPrice:'₹500',offerPrice:'₹0',tag:'Free'},{id:402,name:'Follow-up Visit',category:'Consultations',originalPrice:'₹500',offerPrice:'₹300',tag:''},{id:403,name:'Complete Blood Count (CBC)',category:'Diagnostics',originalPrice:'₹450',offerPrice:'₹360',tag:'Lab'}]},
  { id:5, shopName:'Modern Woodworks',  category:'Furniture',           title:'Exchange Offer: Up to ₹5000 Off',        description:'Bring your old sofa and get an instant discount on our premium teakwood collection.',               distance:12.4,validUntil:'Oct 20, 2025', rating:4.4, reviews:88,   icon:'🛋️', discountPercent:25,  endingToday:false, reviewsList:generateReviews(4.4),menu:[{id:501,name:'3-Seater Teak Sofa',category:'Living Room',originalPrice:'₹25,000',offerPrice:'₹20,000',tag:'Exchange'},{id:502,name:'Coffee Table',category:'Living Room',originalPrice:'₹4,500',offerPrice:'₹3,800',tag:''}]},
  { id:6, shopName:'SuperMart Grocery', category:'Groceries',           title:'Flash Sale: 75% Off Household Items',    description:'Massive clearance on cleaning supplies, staples, and packaged foods.',                              distance:0.8, validUntil:'Today, 11:59 PM',rating:4.6, reviews:2100, icon:'🛒', discountPercent:75,  endingToday:true,  reviewsList:generateReviews(4.6),menu:[{id:601,name:'Detergent 5kg',category:'Household',originalPrice:'₹800',offerPrice:'₹200',tag:'Clearance'},{id:602,name:'Basmati Rice 10kg',category:'Staples',originalPrice:'₹1,200',offerPrice:'₹950',tag:''}]},
  { id:7, shopName:'Burger King',       category:'Food & Beverage',    title:'70% Off on Combo Meals',                 description:'Valid on Whopper combos. Dine-in only.',                                                          distance:0.5, validUntil:'Oct 05, 2025', rating:4.3, reviews:3400, icon:'🍔', discountPercent:70,  endingToday:false, reviewsList:generateReviews(4.3),menu:[{id:701,name:'Whopper Meal',category:'Combos',originalPrice:'₹350',offerPrice:'₹105',tag:'Steal Deal'},{id:702,name:'Fries (Large)',category:'Sides',originalPrice:'₹120',offerPrice:'₹120',tag:''}]},
  { id:8, shopName:'FitLife Gym',       category:'Gyms & Fitness',     title:'70% Off Annual Membership',              description:'Get our premium annual pass including personal training at 70% off. Limited seats.',               distance:2.1, validUntil:'Today, 10:00 PM',rating:4.8, reviews:430,  icon:'💪', discountPercent:70,  endingToday:true,  reviewsList:generateReviews(4.8),menu:[{id:801,name:'Annual Pass',category:'Memberships',originalPrice:'₹20,000',offerPrice:'₹6,000',tag:'Special'},{id:802,name:'Personal Training (1 Month)',category:'Training',originalPrice:'₹5,000',offerPrice:'₹3,500',tag:''}]},
];

const exploreCategories = [
  { id:'Food & Beverage',   icon:<Coffee size={22}/>,        desc:'Restaurants, Cafes, Bakeries',     grad:'from-orange-500 to-red-500' },
  { id:'Electronics',       icon:<Smartphone size={22}/>,    desc:'Mobiles, Laptops, Accessories',    grad:'from-blue-500 to-cyan-500' },
  { id:'Clothing',          icon:<Shirt size={22}/>,         desc:'Men, Women, Kids apparel',          grad:'from-pink-500 to-rose-500' },
  { id:'Groceries',         icon:<ShoppingBasket size={22}/>,desc:'Supermarkets, Daily Needs',         grad:'from-green-500 to-emerald-500' },
  { id:'Salons & Spas',     icon:<Scissors size={22}/>,      desc:'Haircuts, Massage, Beauty',         grad:'from-purple-500 to-violet-500' },
  { id:'Doctors & Clinics', icon:<Stethoscope size={22}/>,   desc:'General Physicians, Dentists',      grad:'from-teal-500 to-cyan-600' },
  { id:'Advocates',         icon:<Scale size={22}/>,         desc:'Legal Advice, Notary',              grad:'from-slate-500 to-slate-700' },
  { id:'Furniture',         icon:<Armchair size={22}/>,      desc:'Home & Office Decor',               grad:'from-amber-500 to-orange-600' },
  { id:'Home Services',     icon:<Wrench size={22}/>,        desc:'Plumbers, Electricians, Cleaning',  grad:'from-yellow-500 to-amber-500' },
  { id:'Gyms & Fitness',    icon:<Dumbbell size={22}/>,      desc:'Yoga, Crossfit, Gyms',              grad:'from-red-500 to-orange-500' },
  { id:'Automotive',        icon:<Car size={22}/>,           desc:'Garages, Wash, Spares',             grad:'from-indigo-500 to-blue-600' },
  { id:'Others',            icon:<MoreHorizontal size={22}/>,desc:'More services & items',             grad:'from-gray-500 to-gray-700' },
];

const specialFilters = [
  { id:'none',         label:'All',          icon:null },
  { id:'megaDiscount', label:'70%+ Off',     icon:<Percent size={12}/> },
  { id:'endingToday',  label:'Ends Today',   icon:<Flame size={12}/> },
  { id:'topRated',     label:'Top Rated',    icon:<Star size={12}/> },
];

// ─────────────────────────────────────────
// 3 PREMIUM THEMES  (19 kept as extras below)
// ─────────────────────────────────────────
const themes = {
  dark: {
    name:'Premium Dark',
    app:'bg-[#090E1A] text-white',
    card:'bg-[#111827]/90 border-white/[0.06] backdrop-blur-sm',
    header:'bg-[#090E1A]/95 border-white/[0.06] backdrop-blur-xl',
    nav:'bg-[#111827]/95 border-white/[0.06] backdrop-blur-xl',
    textMain:'text-white',
    textSec:'text-slate-400',
    accent:'text-indigo-400',
    accentBg:'bg-indigo-600 text-white',
    accentGrad:'bg-gradient-to-r from-indigo-600 to-violet-600',
    accentLight:'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
    border:'border-white/[0.06]',
    inputBg:'bg-white/[0.05]',
    divider:'border-white/[0.06]',
    tag:'bg-white/[0.08] text-slate-300',
    shimmer:'bg-white/[0.04]',
  },
  light: {
    name:'Clean Light',
    app:'bg-slate-50 text-slate-900',
    card:'bg-white border-slate-200',
    header:'bg-white/95 border-slate-200 backdrop-blur-xl',
    nav:'bg-white/95 border-slate-200 backdrop-blur-xl',
    textMain:'text-slate-900',
    textSec:'text-slate-500',
    accent:'text-indigo-600',
    accentBg:'bg-indigo-600 text-white',
    accentGrad:'bg-gradient-to-r from-indigo-600 to-violet-600',
    accentLight:'bg-indigo-50 text-indigo-700 border-indigo-100',
    border:'border-slate-200',
    inputBg:'bg-slate-100',
    divider:'border-slate-100',
    tag:'bg-slate-100 text-slate-600',
    shimmer:'bg-slate-100',
  },
  saffron: {
    name:'Vibrant India',
    app:'bg-[#FFF8F0] text-[#1C1410]',
    card:'bg-white border-orange-100',
    header:'bg-white/95 border-orange-100 backdrop-blur-xl',
    nav:'bg-white/95 border-orange-100 backdrop-blur-xl',
    textMain:'text-[#1C1410]',
    textSec:'text-[#8C6E56]',
    accent:'text-orange-500',
    accentBg:'bg-gradient-to-r from-orange-500 to-amber-400 text-white',
    accentGrad:'bg-gradient-to-r from-orange-500 to-amber-400',
    accentLight:'bg-orange-50 text-orange-700 border-orange-200',
    border:'border-orange-100',
    inputBg:'bg-orange-50/60',
    divider:'border-orange-50',
    tag:'bg-orange-50 text-orange-700',
    shimmer:'bg-orange-50',
  },
};

// ─────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────
const ratingColor = (r) => r >= 4.5 ? 'bg-green-500' : r >= 4 ? 'bg-yellow-500' : 'bg-orange-500';

const categoryGradient = (cat) => {
  const map = { 'Food & Beverage':'from-orange-500 to-red-500', 'Electronics':'from-blue-500 to-cyan-500', 'Clothing':'from-pink-500 to-rose-500', 'Groceries':'from-green-500 to-emerald-500', 'Salons & Spas':'from-purple-500 to-violet-500', 'Doctors & Clinics':'from-teal-500 to-cyan-600', 'Gyms & Fitness':'from-red-500 to-orange-500', 'Automotive':'from-indigo-500 to-blue-600', 'Furniture':'from-amber-500 to-orange-600' };
  return map[cat] || 'from-slate-500 to-slate-700';
};

// ─────────────────────────────────────────
// TOAST
// ─────────────────────────────────────────
const Toast = ({ message, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-24 left-1/2 -translate-x-1/2 z-[999] px-5 py-3 rounded-2xl font-semibold shadow-2xl flex items-center gap-2 text-sm text-white ${type === 'error' ? 'bg-red-500' : 'bg-green-600'}`}>
      {type === 'error' ? <AlertCircle size={16}/> : <Check size={16}/>}
      {message}
    </div>
  );
};

// ─────────────────────────────────────────
// OFFER CARD  — Zomato-inspired
// ─────────────────────────────────────────
const OfferCard = ({ offer, t, onClick }) => {
  const grad = categoryGradient(offer.category);
  return (
    <div onClick={onClick} className={`${t.card} border rounded-2xl overflow-hidden cursor-pointer active:scale-[0.98] transition-all duration-200 hover:shadow-xl relative group`}>
      {/* Urgency bar */}
      {offer.endingToday && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 to-red-500 z-10" />
      )}

      {/* Icon zone with gradient */}
      <div className={`relative h-32 bg-gradient-to-br ${grad} flex items-center justify-center overflow-hidden`}>
        <div className="absolute inset-0 bg-black/20" />
        <span className="text-6xl relative z-10 drop-shadow-lg">{offer.icon}</span>

        {/* Discount badge — floating top right */}
        <div className="absolute top-3 right-3 z-20">
          <div className={`bg-white text-sm font-black px-2.5 py-1 rounded-xl shadow-lg ${offer.discountPercent >= 70 ? 'text-red-600' : 'text-orange-600'}`}>
            {offer.discountPercent}% OFF
          </div>
        </div>

        {/* Ending today badge */}
        {offer.endingToday && (
          <div className="absolute top-3 left-3 z-20 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-lg flex items-center gap-1 animate-pulse shadow-lg">
            <Flame size={11}/> Ends Today
          </div>
        )}

        {/* Category chip — bottom left */}
        <div className="absolute bottom-3 left-3 z-20 bg-black/40 backdrop-blur-sm text-white text-xs font-medium px-2 py-0.5 rounded-full">
          {offer.category}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <p className={`text-xs font-semibold mb-1 ${t.accent}`}>{offer.shopName}</p>
        <h3 className={`font-bold text-sm leading-snug mb-2 ${t.textMain}`}>{offer.title}</h3>
        <p className={`text-xs leading-relaxed line-clamp-2 mb-3 ${t.textSec}`}>{offer.description}</p>

        {/* Footer row */}
        <div className={`flex items-center justify-between pt-3 border-t ${t.divider}`}>
          {/* Rating — Zomato style */}
          <div className="flex items-center gap-1.5">
            <span className={`text-white text-xs font-bold px-1.5 py-0.5 rounded-md ${ratingColor(offer.rating)}`}>
              ★ {offer.rating}
            </span>
            <span className={`text-xs ${t.textSec}`}>{offer.reviews.toLocaleString()} reviews</span>
          </div>

          {/* Distance */}
          <div className={`flex items-center gap-1 text-xs ${t.textSec}`}>
            <MapPin size={11}/>
            <span>{offer.distance} km</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────
// CATEGORY CARD  — Urban Company style
// ─────────────────────────────────────────
const CategoryCard = ({ cat, t, onClick }) => (
  <div onClick={() => onClick(cat.id)} className={`${t.card} border rounded-2xl p-4 cursor-pointer active:scale-[0.97] transition-all duration-200 hover:shadow-lg flex flex-col items-center gap-3 text-center`}>
    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${cat.grad} flex items-center justify-center shadow-lg`}>
      <span className="text-white">{cat.icon}</span>
    </div>
    <div>
      <p className={`text-sm font-bold leading-tight ${t.textMain}`}>{cat.id}</p>
      <p className={`text-xs mt-0.5 leading-snug ${t.textSec}`}>{cat.desc}</p>
    </div>
  </div>
);

// ─────────────────────────────────────────
// AUTH MODAL  — bottom-sheet style
// ─────────────────────────────────────────
const AuthModal = ({ isOpen, onClose, t, onSuccess }) => {
  const [view, setView] = useState('login');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Please fill all fields'); return; }
    setError(''); setLoading(true);
    setTimeout(() => { setLoading(false); onSuccess?.(); onClose(); }, 1500);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[500] flex items-end justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative ${t.card.replace('border','')} border-t ${t.border} w-full max-w-md rounded-t-3xl px-6 pt-4 pb-8 z-10 shadow-2xl`}>
        {/* Handle */}
        <div className={`w-10 h-1 rounded-full ${t.shimmer} mx-auto mb-6`} />

        {/* Tabs */}
        <div className={`flex rounded-xl p-1 ${t.inputBg} mb-6`}>
          {['login','register'].map(v => (
            <button key={v} onClick={() => { setView(v); setError(''); }}
              className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all ${view === v ? `${t.accentBg} shadow` : t.textSec}`}>
              {v === 'login' ? 'Sign In' : 'Register'}
            </button>
          ))}
        </div>

        {error && (
          <div className="flex items-center gap-2 text-red-500 text-xs font-medium mb-4 bg-red-500/10 px-3 py-2 rounded-xl">
            <AlertCircle size={14}/> {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          {view === 'register' && (
            <div className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border ${t.border} ${t.inputBg}`}>
              <User size={16} className={t.textSec} />
              <input value={name} onChange={e=>setName(e.target.value)} placeholder="Full name" className={`flex-1 bg-transparent text-sm outline-none ${t.textMain} placeholder:${t.textSec}`} />
            </div>
          )}

          <div className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border ${t.border} ${t.inputBg}`}>
            <Mail size={16} className={t.textSec} />
            <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="Email address" className={`flex-1 bg-transparent text-sm outline-none ${t.textMain} placeholder:${t.textSec}`} />
          </div>

          <div className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border ${t.border} ${t.inputBg}`}>
            <div className={t.textSec}><svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg></div>
            <input type={showPwd ? 'text' : 'password'} value={password} onChange={e=>setPassword(e.target.value)} placeholder="Password" className={`flex-1 bg-transparent text-sm outline-none ${t.textMain} placeholder:${t.textSec}`} />
            <button type="button" onClick={() => setShowPwd(!showPwd)} className={t.textSec}>
              {showPwd ? <EyeOff size={16}/> : <Eye size={16}/>}
            </button>
          </div>

          <button type="submit" disabled={loading} className={`w-full py-4 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 ${t.accentGrad} text-white shadow-lg active:scale-[0.98] transition-all disabled:opacity-60`}>
            {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"/> : view === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        <div className="flex items-center gap-3 my-4">
          <div className={`flex-1 h-px ${t.divider} border-t`}/>
          <span className={`text-xs ${t.textSec}`}>or continue with</span>
          <div className={`flex-1 h-px ${t.divider} border-t`}/>
        </div>

        {/* Google button */}
        <button className={`w-full py-3.5 rounded-2xl border ${t.border} ${t.card} flex items-center justify-center gap-3 font-semibold text-sm ${t.textMain} active:scale-[0.98] transition-all`}>
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────
// OFFER DETAIL VIEW
// ─────────────────────────────────────────
const DetailView = ({ offer, t, onBack }) => {
  const [tab, setTab] = useState('menu');
  const grad = categoryGradient(offer.category);

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Hero */}
      <div className={`relative h-56 bg-gradient-to-br ${grad} flex items-center justify-center flex-shrink-0`}>
        <div className="absolute inset-0 bg-black/30" />
        <span className="text-8xl relative z-10 drop-shadow-xl">{offer.icon}</span>

        {/* Back button */}
        <button onClick={onBack} className="absolute top-4 left-4 z-20 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white">
          <ArrowLeft size={20}/>
        </button>

        {/* Discount badge */}
        <div className="absolute top-4 right-4 z-20 bg-white rounded-2xl px-3 py-1.5 shadow-lg">
          <span className="text-red-600 font-black text-lg">{offer.discountPercent}%</span>
          <span className="text-red-600 font-bold text-xs"> OFF</span>
        </div>

        {offer.endingToday && (
          <div className="absolute bottom-4 left-4 z-20 bg-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 animate-pulse">
            <Flame size={12}/> Ending Today
          </div>
        )}
      </div>

      {/* Info card */}
      <div className="px-4 -mt-5 relative z-10">
        <div className={`${t.card} border rounded-2xl p-4 shadow-xl`}>
          <p className={`text-xs font-semibold mb-1 ${t.accent}`}>{offer.shopName}</p>
          <h2 className={`font-bold text-lg leading-tight mb-2 ${t.textMain}`}>{offer.title}</h2>
          <p className={`text-sm leading-relaxed ${t.textSec}`}>{offer.description}</p>

          <div className={`flex items-center gap-4 mt-4 pt-4 border-t ${t.divider}`}>
            <div className="flex items-center gap-1.5">
              <span className={`text-white text-xs font-bold px-2 py-1 rounded-lg ${ratingColor(offer.rating)}`}>★ {offer.rating}</span>
              <span className={`text-xs ${t.textSec}`}>{offer.reviews.toLocaleString()} reviews</span>
            </div>
            <div className={`flex items-center gap-1 text-xs ${t.textSec}`}><MapPin size={12}/>{offer.distance} km away</div>
            <div className={`flex items-center gap-1 text-xs ${t.textSec}`}><Clock size={12}/> Valid till {offer.validUntil}</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className={`flex gap-2 px-4 mt-4 sticky top-0 z-20 py-2 ${t.app}`}>
        {['menu','reviews'].map(tb => (
          <button key={tb} onClick={() => setTab(tb)}
            className={`px-5 py-2 rounded-xl text-sm font-bold transition-all ${tab === tb ? `${t.accentBg} shadow` : `${t.tag} ${t.textSec}`}`}>
            {tb === 'menu' ? '🍽 Menu' : '⭐ Reviews'}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="px-4 pb-32 mt-2 space-y-3">
        {tab === 'menu' ? (
          offer.menu.map(item => (
            <div key={item.id} className={`${t.card} border rounded-2xl p-4 flex items-center justify-between`}>
              <div className="flex-1 pr-4">
                <div className="flex items-center gap-2 mb-1">
                  <p className={`font-bold text-sm ${t.textMain}`}>{item.name}</p>
                  {item.tag && <span className={`text-xs px-2 py-0.5 rounded-full ${t.accentLight} border font-medium`}>{item.tag}</span>}
                </div>
                <p className={`text-xs ${t.textSec}`}>{item.category}</p>
              </div>
              <div className="text-right">
                <p className={`font-black text-base ${t.accent}`}>{item.offerPrice}</p>
                {item.offerPrice !== item.originalPrice && (
                  <p className={`text-xs line-through ${t.textSec}`}>{item.originalPrice}</p>
                )}
              </div>
            </div>
          ))
        ) : (
          offer.reviewsList.map(rev => (
            <div key={rev.id} className={`${t.card} border rounded-2xl p-4`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full ${t.accentGrad} flex items-center justify-center text-white text-xs font-bold`}>
                    {rev.user[0]}
                  </div>
                  <div>
                    <p className={`text-sm font-bold ${t.textMain}`}>{rev.user}</p>
                    <p className={`text-xs ${t.textSec}`}>{rev.date}</p>
                  </div>
                </div>
                <span className={`text-white text-xs font-bold px-2 py-0.5 rounded-lg ${ratingColor(rev.rating)}`}>★ {rev.rating}</span>
              </div>
              <p className={`text-sm leading-relaxed ${t.textSec}`}>{rev.text}</p>
            </div>
          ))
        )}
      </div>

      {/* Sticky CTA */}
      <div className={`fixed bottom-0 left-0 right-0 p-4 ${t.nav} border-t z-30`}>
        <button className={`w-full py-4 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 ${t.accentGrad} text-white shadow-xl`}>
          <Navigation size={16}/> Get Directions
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────
// AI CHAT VIEW
// ─────────────────────────────────────────
const AIChatView = ({ t }) => {
  const [messages, setMessages] = useState([
    { role:'ai', text:'Hi! I\'m your Nearbeee AI. Ask me to find nearby shops, check today\'s best offers, or help you book a table. 🏪' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  const suggestions = ['Biryani near me 🍲', 'Best gym offers 💪', 'Ending today deals 🔥', 'Top-rated salons ✂️'];

  const send = (text) => {
    const msg = text || input.trim();
    if (!msg) return;
    setMessages(prev => [...prev, { role:'user', text: msg }]);
    setInput('');
    setLoading(true);
    setTimeout(() => {
      setMessages(prev => [...prev, { role:'ai', text:`Searching nearby for "${msg}"... Found 3 great options within 2km! Tap the offers tab to see results sorted by distance.` }]);
      setLoading(false);
    }, 1200);
  };

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:'smooth' }); }, [messages, loading]);

  return (
    <div className="flex flex-col h-full">
      {/* Chat area */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 pb-40">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
            {m.role === 'ai' && (
              <div className={`w-9 h-9 rounded-2xl ${t.accentGrad} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                <Bot size={18} className="text-white"/>
              </div>
            )}
            <div className={`max-w-[78%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
              m.role === 'user'
                ? `${t.accentGrad} text-white rounded-tr-sm`
                : `${t.card} border ${t.border} ${t.textMain} rounded-tl-sm`
            }`}>
              {m.text}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex gap-3">
            <div className={`w-9 h-9 rounded-2xl ${t.accentGrad} flex items-center justify-center flex-shrink-0`}>
              <Bot size={18} className="text-white"/>
            </div>
            <div className={`${t.card} border ${t.border} px-4 py-3 rounded-2xl rounded-tl-sm flex gap-1 items-center`}>
              {[0,1,2].map(d => <div key={d} className={`w-2 h-2 rounded-full ${t.accent.replace('text-','bg-')} animate-bounce`} style={{animationDelay:`${d*0.15}s`}}/>)}
            </div>
          </div>
        )}
        <div ref={bottomRef}/>
      </div>

      {/* Suggestion chips */}
      <div className={`px-4 py-2 flex gap-2 overflow-x-auto scrollbar-none ${t.app}`}>
        {suggestions.map(s => (
          <button key={s} onClick={() => send(s)} className={`flex-shrink-0 text-xs px-3 py-2 rounded-full border ${t.border} ${t.tag} font-medium whitespace-nowrap`}>{s}</button>
        ))}
      </div>

      {/* Input */}
      <div className={`px-4 pb-24 pt-2 ${t.app}`}>
        <div className={`flex items-center gap-3 ${t.card} border ${t.border} rounded-2xl px-4 py-3 shadow-lg`}>
          <input
            value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && send()}
            placeholder="Ask anything nearby..."
            className={`flex-1 bg-transparent text-sm outline-none ${t.textMain} placeholder:opacity-50`}
          />
          <button onClick={() => send()} disabled={!input.trim()} className={`w-9 h-9 rounded-xl ${t.accentGrad} flex items-center justify-center text-white disabled:opacity-40 transition-all`}>
            <Send size={15}/>
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────
// PROFILE VIEW
// ─────────────────────────────────────────
const ProfileView = ({ t, onLogin }) => {
  const isLoggedIn = false;

  const menuItems = [
    { icon:<Tag size={18}/>,          label:'My Bookings',      sub:'0 bookings' },
    { icon:<Heart size={18}/>,        label:'Saved Offers',     sub:'0 saved' },
    { icon:<MapPin size={18}/>,       label:'Saved Addresses',  sub:'Add address' },
    { icon:<ShieldCheck size={18}/>,  label:'Privacy & Security',sub:'' },
    { icon:<Settings size={18}/>,     label:'App Settings',     sub:'' },
  ];

  return (
    <div className="px-4 py-6 pb-32 space-y-4">
      {isLoggedIn ? (
        <div className={`${t.card} border ${t.border} rounded-3xl p-5 flex items-center gap-4`}>
          <div className={`w-16 h-16 rounded-2xl ${t.accentGrad} flex items-center justify-center text-white text-2xl font-black shadow-lg`}>A</div>
          <div>
            <p className={`font-bold text-lg ${t.textMain}`}>Aman Kumar</p>
            <p className={`text-sm ${t.textSec}`}>aman@example.com</p>
          </div>
        </div>
      ) : (
        <div className={`${t.card} border ${t.border} rounded-3xl p-6 text-center`}>
          <div className={`w-20 h-20 rounded-3xl ${t.accentGrad} flex items-center justify-center mx-auto mb-4 shadow-xl`}>
            <User size={36} className="text-white"/>
          </div>
          <p className={`font-bold text-lg mb-1 ${t.textMain}`}>Sign in to Nearbeee</p>
          <p className={`text-sm mb-5 ${t.textSec}`}>Get personalised offers & book your favourite spots</p>
          <button onClick={onLogin} className={`w-full py-3.5 rounded-2xl font-bold text-sm ${t.accentGrad} text-white shadow-lg`}>
            Sign In / Register
          </button>
        </div>
      )}

      <div className={`${t.card} border ${t.border} rounded-3xl overflow-hidden`}>
        {menuItems.map((item, i) => (
          <div key={i} className={`flex items-center gap-4 px-5 py-4 cursor-pointer active:opacity-70 ${i < menuItems.length-1 ? `border-b ${t.divider}` : ''}`}>
            <div className={`w-10 h-10 rounded-xl ${t.shimmer} flex items-center justify-center ${t.textSec}`}>{item.icon}</div>
            <div className="flex-1">
              <p className={`text-sm font-semibold ${t.textMain}`}>{item.label}</p>
              {item.sub && <p className={`text-xs ${t.textSec}`}>{item.sub}</p>}
            </div>
            <ChevronRight size={16} className={t.textSec}/>
          </div>
        ))}
      </div>

      <div className={`${t.card} border ${t.border} rounded-3xl overflow-hidden`}>
        <div className="flex items-center gap-4 px-5 py-4 cursor-pointer text-red-500">
          <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center"><LogOut size={18}/></div>
          <p className="text-sm font-semibold">Sign Out</p>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────
// HOME VIEW  — Swiggy / Zomato inspired
// ─────────────────────────────────────────
const HomeView = ({ t, onOfferClick, onCategoryClick, onLogin }) => {
  const [filter, setFilter] = useState('none');
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('offers');

  const filtered = mockOffers.filter(o => {
    const matchSearch = o.shopName.toLowerCase().includes(search.toLowerCase()) || o.title.toLowerCase().includes(search.toLowerCase());
    if (!matchSearch) return false;
    if (filter === 'megaDiscount') return o.discountPercent >= 70;
    if (filter === 'endingToday') return o.endingToday;
    if (filter === 'topRated') return o.rating >= 4.5;
    return true;
  });

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Search bar */}
      <div className="px-4 pt-4 pb-2">
        <div className={`flex items-center gap-3 ${t.inputBg} border ${t.border} rounded-2xl px-4 py-3.5 shadow-sm`}>
          <Search size={18} className={t.textSec}/>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search offers, shops, categories..."
            className={`flex-1 bg-transparent text-sm outline-none ${t.textMain} placeholder:opacity-50`}
          />
          {search ? (
            <button onClick={() => setSearch('')}><X size={16} className={t.textSec}/></button>
          ) : (
            <div className={`flex items-center gap-1 text-xs ${t.accent} font-semibold`}><Zap size={13}/> AI</div>
          )}
        </div>
      </div>

      {/* Quick category pills — horizontal scroll */}
      <div className="px-4 pb-3 flex gap-2 overflow-x-auto scrollbar-none">
        {exploreCategories.slice(0, 8).map(cat => (
          <button key={cat.id} onClick={() => onCategoryClick(cat.id)}
            className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl border ${t.border} ${t.card} text-xs font-semibold ${t.textMain} active:scale-95 transition-all`}>
            <span className={`w-6 h-6 rounded-lg bg-gradient-to-br ${cat.grad} flex items-center justify-center`}>
              <span className="text-white scale-75">{cat.icon}</span>
            </span>
            {cat.id.split(' ')[0]}
          </button>
        ))}
      </div>

      {/* View tabs */}
      <div className={`flex gap-2 px-4 pb-3`}>
        {[{id:'offers',label:'🏷 Offers'},{id:'explore',label:'🧭 Explore'}].map(tb => (
          <button key={tb.id} onClick={() => setTab(tb.id)}
            className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${tab === tb.id ? `${t.accentBg} shadow-lg` : `${t.tag} ${t.textSec}`}`}>
            {tb.label}
          </button>
        ))}
      </div>

      {tab === 'offers' ? (
        <>
          {/* Special filters */}
          <div className="px-4 pb-3 flex gap-2 overflow-x-auto scrollbar-none">
            {specialFilters.map(f => (
              <button key={f.id} onClick={() => setFilter(f.id === filter ? 'none' : f.id)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold border transition-all ${filter === f.id ? `${t.accentBg} border-transparent shadow` : `${t.card} border ${t.border} ${t.textSec}`}`}>
                {f.icon} {f.label}
              </button>
            ))}
          </div>

          {/* Count */}
          <div className={`px-4 pb-3 text-xs font-semibold ${t.textSec}`}>
            {filtered.length} offer{filtered.length !== 1 ? 's' : ''} near you
          </div>

          {/* Cards grid */}
          <div className="px-4 pb-32 grid grid-cols-1 gap-4">
            {filtered.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-5xl mb-4">🔍</div>
                <p className={`font-bold text-base mb-1 ${t.textMain}`}>No offers found</p>
                <p className={`text-sm ${t.textSec}`}>Try a different filter or search term</p>
              </div>
            ) : filtered.map(o => (
              <OfferCard key={o.id} offer={o} t={t} onClick={() => onOfferClick(o)}/>
            ))}
          </div>
        </>
      ) : (
        <div className="px-4 pb-32 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {exploreCategories.map(cat => (
            <CategoryCard key={cat.id} cat={cat} t={t} onClick={onCategoryClick}/>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────
// BOTTOM NAVIGATION  — floating glass pill
// ─────────────────────────────────────────
const BottomNav = ({ active, onChange, t }) => {
  const tabs = [
    { id:'home',    icon:<Tag size={22}/>,           label:'Offers'  },
    { id:'explore', icon:<Store size={22}/>,          label:'Explore' },
    { id:'chat',    icon:<MessageCircle size={22}/>,  label:'AI Chat', primary:true },
    { id:'profile', icon:<User size={22}/>,           label:'Account' },
  ];

  return (
    <div className={`fixed bottom-4 left-4 right-4 z-50 ${t.nav} border rounded-3xl px-2 py-2 shadow-2xl flex items-center justify-around`}>
      {tabs.map(tab => (
        <button key={tab.id} onClick={() => onChange(tab.id)}
          className={`flex flex-col items-center gap-1 py-1 px-3 rounded-2xl transition-all duration-200 ${
            active === tab.id
              ? tab.primary
                ? `${t.accentGrad} text-white shadow-lg px-5`
                : `${t.accentLight} border`
              : `${t.textSec} opacity-70`
          }`}>
          {tab.icon}
          <span className="text-[10px] font-bold">{tab.label}</span>
        </button>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────
// HEADER  — Swiggy location pattern
// ─────────────────────────────────────────
const Header = ({ t, themeName, onThemeToggle, onLoginClick }) => (
  <div className={`${t.header} border-b sticky top-0 z-40 flex items-center justify-between px-4 py-3`}>
    {/* Location pill */}
    <button className={`flex items-center gap-2 ${t.textMain}`}>
      <MapPin size={16} className={t.accent}/>
      <div className="text-left">
        <p className="text-xs font-black tracking-widest uppercase text-opacity-60">Near you</p>
        <div className={`flex items-center gap-1 text-sm font-bold ${t.textMain}`}>
          Koramangala <ChevronDown size={14} className={t.textSec}/>
        </div>
      </div>
    </button>

    {/* Logo */}
    <div className={`font-black text-xl tracking-tight ${t.accent}`}>
      Nearbe<span className="opacity-50">eee</span>
    </div>

    {/* Actions */}
    <div className="flex items-center gap-2">
      <button onClick={onThemeToggle} className={`w-9 h-9 rounded-xl ${t.inputBg} border ${t.border} flex items-center justify-center ${t.textSec}`}>
        <Palette size={16}/>
      </button>
      <button onClick={onLoginClick} className={`w-9 h-9 rounded-xl ${t.inputBg} border ${t.border} flex items-center justify-center ${t.textSec}`}>
        <User size={16}/>
      </button>
    </div>
  </div>
);

// ─────────────────────────────────────────
// MAIN APP
// ─────────────────────────────────────────
const THEME_ORDER = ['dark', 'light', 'saffron'];

export default function NearbeeeApp() {
  const [themeKey, setThemeKey]       = useState('dark');
  const [activeTab, setActiveTab]     = useState('home');
  const [selectedOffer, setOffer]     = useState(null);
  const [authOpen, setAuthOpen]       = useState(false);
  const [toast, setToast]             = useState(null);

  const t = themes[themeKey];
  const nextTheme = () => setThemeKey(k => THEME_ORDER[(THEME_ORDER.indexOf(k) + 1) % THEME_ORDER.length]);

  const showToast = (message, type = 'success') => setToast({ message, type });

  // If offer detail is open, show that instead of tab content
  const renderContent = () => {
    if (selectedOffer) {
      return <DetailView offer={selectedOffer} t={t} onBack={() => setOffer(null)}/>;
    }
    switch (activeTab) {
      case 'home':
        return <HomeView t={t} onOfferClick={setOffer} onCategoryClick={(cat) => { showToast(`Browsing: ${cat}`); }} onLogin={() => setAuthOpen(true)}/>;
      case 'explore':
        return (
          <div className="px-4 pt-4 pb-32 grid grid-cols-2 gap-3 sm:grid-cols-3 overflow-y-auto">
            {exploreCategories.map(cat => (
              <CategoryCard key={cat.id} cat={cat} t={t} onClick={(id) => showToast(`Browsing: ${id}`)}/>
            ))}
          </div>
        );
      case 'chat':    return <AIChatView t={t}/>;
      case 'profile': return <ProfileView t={t} onLogin={() => setAuthOpen(true)}/>;
      default:        return null;
    }
  };

  return (
    <div className={`${t.app} min-h-screen font-sans flex flex-col`} style={{ maxWidth: 430, margin: '0 auto', height: '100dvh', overflow: 'hidden', position: 'relative' }}>

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)}/>}

      {/* Auth modal */}
      <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} t={t} onSuccess={() => showToast('Signed in successfully!')}/>

      {/* Header — hidden on detail view */}
      {!selectedOffer && <Header t={t} themeName={t.name} onThemeToggle={nextTheme} onLoginClick={() => setAuthOpen(true)}/>}

      {/* Main scrollable content */}
      <div className="flex-1 overflow-hidden relative">
        {renderContent()}
      </div>

      {/* Bottom nav — hidden on detail */}
      {!selectedOffer && <BottomNav active={activeTab} onChange={setActiveTab} t={t}/>}
    </div>
  );
}
